import * as React from 'react';
import axios from 'axios';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { format, parseISO } from 'date-fns';
import { getAdminApiBaseUrl } from '../utils/adminApiBaseUrl';

export type ReprocessedClickChannel =
    | 'view_inquire'
    | 'message'
    | 'whatsapp'
    | 'call'
    | 'detail_view'
    | 'all';

type SortField = 'customer_name' | 'company_name' | 'phone_mobile' | 'channel' | 'clicked_at';
type SortOrder = 'asc' | 'desc';

interface ReprocessedClickRow {
    event_id?: string;
    customer_id: number | null;
    customer_name: string | null;
    company_name: string | null;
    phone_mobile: string | null;
    customer_username: string | null;
    channel: string;
    clicked_at: string | null;
    visitor_id?: number | null;
}

interface ReprocessedClickDetailModalProps {
    open: boolean;
    listingId: number | null;
    listingTitle: string;
    channel: ReprocessedClickChannel | null;
    channelLabel: string;
    expectedCount?: number;
    fromDate: string;
    toDate: string;
    onClose: () => void;
}

const COLUMNS: Array<{ id: SortField; label: string }> = [
    { id: 'customer_name', label: 'Name' },
    { id: 'company_name', label: 'Company' },
    { id: 'phone_mobile', label: 'Mobile' },
    { id: 'channel', label: 'Channel' },
    { id: 'clicked_at', label: 'Clicked At' },
];

const formatDateTime = (value: string | null) => {
    if (!value) {
        return '—';
    }

    try {
        const parsed = parseISO(value);
        if (Number.isNaN(parsed.getTime())) {
            return value;
        }
        return format(parsed, 'MMM d, yyyy · h:mm a');
    } catch {
        return value;
    }
};

const ReprocessedClickDetailModal: React.FC<ReprocessedClickDetailModalProps> = ({
    open,
    listingId,
    listingTitle,
    channel,
    channelLabel,
    expectedCount,
    fromDate,
    toDate,
    onClose,
}) => {
    const [rows, setRows] = React.useState<ReprocessedClickRow[]>([]);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(50);
    const [sort, setSort] = React.useState<SortField>('clicked_at');
    const [order, setOrder] = React.useState<SortOrder>('desc');
    const [dataThrough, setDataThrough] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!open || !listingId || !channel) {
            return;
        }
        setPage(0);
        setSort('clicked_at');
        setOrder('desc');
        setRows([]);
        setTotal(0);
        setError(null);
    }, [open, listingId, channel, fromDate, toDate]);

    React.useEffect(() => {
        if (!open || !listingId || !channel) {
            return;
        }

        const fetchDetails = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await axios.get(
                    `${getAdminApiBaseUrl()}/analytics/reprocessed-listings/${listingId}/clicks`,
                    {
                        params: {
                            channel,
                            from_date: fromDate,
                            to_date: toDate,
                            limit: rowsPerPage,
                            offset: page * rowsPerPage,
                            sort,
                            order,
                        },
                    }
                );

                setRows(Array.isArray(response.data?.data) ? response.data.data : []);
                setTotal(Number(response.data?.total) || 0);
                setDataThrough(response.data?.data_through || null);
            } catch (fetchError: any) {
                console.error('Failed to load reprocessed listing click details:', fetchError);
                setRows([]);
                setTotal(0);
                setError(fetchError?.response?.data?.message || 'Failed to load click details.');
            } finally {
                setLoading(false);
            }
        };

        fetchDetails();
    }, [open, listingId, channel, fromDate, toDate, page, rowsPerPage, sort, order]);

    const handleSort = (field: SortField) => {
        const isSameField = sort === field;
        setSort(field);
        setOrder(isSameField ? (order === 'asc' ? 'desc' : 'asc') : 'desc');
        setPage(0);
    };

    const titleCount = total || expectedCount || 0;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle sx={{ pr: 6 }}>
                {listingTitle} — {channelLabel} ({titleCount.toLocaleString()})
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 12, top: 12 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {dataThrough && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Data through {format(parseISO(`${dataThrough}T00:00:00`), 'MMM d, yyyy')} (nightly rollup)
                    </Typography>
                )}

                {error ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : loading ? (
                    <Skeleton variant="rectangular" height={360} />
                ) : rows.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">No click events found for this metric.</Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {COLUMNS.map(column => (
                                            <TableCell key={column.id} sortDirection={sort === column.id ? order : false}>
                                                <TableSortLabel
                                                    active={sort === column.id}
                                                    direction={sort === column.id ? order : 'asc'}
                                                    onClick={() => handleSort(column.id)}
                                                >
                                                    {column.label}
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map(row => (
                                        <TableRow key={row.event_id || `${row.clicked_at}-${row.customer_id}-${row.channel}`} hover>
                                            <TableCell>{row.customer_name || row.customer_username || '—'}</TableCell>
                                            <TableCell>{row.company_name || '—'}</TableCell>
                                            <TableCell>{row.phone_mobile || '—'}</TableCell>
                                            <TableCell>{row.channel || '—'}</TableCell>
                                            <TableCell>{formatDateTime(row.clicked_at)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_event, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(event) => {
                                setRowsPerPage(parseInt(event.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[25, 50, 100]}
                        />
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ReprocessedClickDetailModal;
